import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import WebFont from "webfontloader";
import { resolvePosition } from "./templatePositions";
import {
  Canvas,
  Rect,
  Textbox,
  FabricImage,
  Circle,
  Triangle,
  Line,
  ActiveSelection,
} from "fabric";
import {
  Button,
  ButtonGroup,
  Container,
  Row,
  Col,
  Form,
  OverlayTrigger,
  Tooltip,
  Dropdown,
} from "react-bootstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  FaUndo,
  FaRedo,
  FaFont,
  FaSquare,
  FaCircle,
  FaUpload,
  FaBold,
  FaItalic,
  FaMinus,
  FaArrowUp,
  FaArrowDown,
  FaFileImage,
  FaFileCode,
  FaTrash,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaExpand,
} from "react-icons/fa";
import { GiTriangleTarget } from "react-icons/gi";
import { Sketch } from "@uiw/react-color";

// -------------------------
// Font Options and Select Styles
// -------------------------
const fontOptions = [
  { value: "Roboto", label: "Roboto" },
  { value: "Pacifico", label: "Pacifico" },
  { value: "Lobster", label: "Lobster" },
  { value: "Bangers", label: "Bangers" },
  { value: "Fredoka One", label: "Fredoka One" },
  { value: "Indie Flower", label: "Indie Flower" },
  { value: "Oswald", label: "Oswald" },
  { value: "Amatic SC", label: "Amatic SC" },
  { value: "Chewy", label: "Chewy" },
  { value: "Special Elite", label: "Special Elite" },
  { value: "Dancing Script", label: "Dancing Script" },
  { value: "Great Vibes", label: "Great Vibes" },
  { value: "Merienda", label: "Merienda" },
  { value: "Montserrat", label: "Montserrat" },
  { value: "Raleway", label: "Raleway" },
  { value: "Playfair Display", label: "Playfair Display" },
  { value: "Lora", label: "Lora" },
  { value: "Abril Fatface", label: "Abril Fatface" },
  { value: "Caveat", label: "Caveat" },
  { value: "Satisfy", label: "Satisfy" },
  // ... add more if desired
];

const customSelectStyles = {
  option: (provided, state) => ({
    ...provided,
    fontFamily: state.data.value,
    fontSize: "0.8rem",
  }),
  singleValue: (provided, state) => ({
    ...provided,
    fontFamily: state.data.value,
    fontSize: "0.8rem",
  }),
  control: (provided) => ({
    ...provided,
    minWidth: "150px",
    fontSize: "0.8rem",
  }),
};

const MAX_UNDO_STEPS = 50;

// -------------------------
// Font Size Options for the dropdown
// -------------------------
const fontSizeOptions = [
  { value: 8, label: "8" },
  { value: 10, label: "10" },
  { value: 12, label: "12" },
  { value: 14, label: "14" },
  { value: 16, label: "16" },
  { value: 18, label: "18" },
  { value: 20, label: "20" },
  { value: 24, label: "24" },
  { value: 28, label: "28" },
  { value: 32, label: "32" },
  { value: 36, label: "36" },
  { value: 48, label: "48" },
  { value: 60, label: "60" },
  { value: 72, label: "72" },
  { value: 84, label: "84" },
  { value: 96, label: "96" },
  { value: 120, label: "120" },
  { value: 144, label: "144" },
  { value: 168, label: "168" },
  { value: 200, label: "200" },
  { value: 250, label: "250" },
  { value: 300, label: "300" },
];

// Zoom level options for the dropdown
const zoomOptions = [
  { value: 100, label: "100%" },
  { value: 125, label: "125%" },
  { value: 150, label: "150%" },
  { value: 200, label: "200%" },
  { value: 300, label: "300%" },
  { value: 400, label: "400%" },
  { value: 500, label: "500%" },
];

const PUSH_INTERVAL = 30000; // 30 seconds

// Flag to control zoom functionality
const ENABLE_ZOOM = false;

// -------------------------
// Main Component
// -------------------------
const MoviePosterEditor = (props) => {
  // Load Google Fonts via WebFont Loader
  useEffect(() => {
    WebFont.load({
      google: {
        families: [
          "Roboto:400,700",
          "Pacifico",
          "Lobster",
          "Bangers",
          "Fredoka One",
          "Indie Flower",
          "Oswald:400,700",
          "Amatic SC:400,700",
          "Chewy",
          "Special Elite",
          "Dancing Script",
          "Great Vibes",
          "Merienda",
          "Montserrat:400,700",
          "Raleway:400,700",
          "Playfair Display:400,700",
          "Lora:400,700",
          "Abril Fatface",
          "Caveat",
          "Satisfy",
          // ... add additional fonts if desired
        ],
      },
    });
  }, []);

  // Refs for canvas and file input
  const canvasEl = useRef(null);
  const fileInputRef = useRef(null);
  // Clipboard for copy-paste functionality
  const clipboard = useRef(null);

  const lastPushTimeRef = useRef(0);
  const timerRef = useRef(null);

  const hasLoadedFromStorage = useRef(false);

  // State for showing the color pickers
  const [showStrokePicker, setShowStrokePicker] = useState(false);

  // Undo/redo stacks and restoration flag
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const currentStateJson = useRef(null);
  const isRestoring = useRef(false);

  // State for the currently selected object (if any single object is selected)
  const [selectedObj, setSelectedObj] = useState(null);
  // State for text properties (added textAlign with default "left")
  const [textProperties, setTextProperties] = useState({
    fontSize: 20,
    fill: "#000000",
    fontFamily: "Roboto",
    fontStyle: "normal",
    fontWeight: "normal",
    stroke: "#000000",
    strokeWidth: 0,
    textAlign: "left",
  });
  // State for shape properties
  const [shapeProperties, setShapeProperties] = useState({
    fill: "#ffffff",
    stroke: "#000000",
    strokeWidth: 1,
  });
  // State for zoom level
  const [zoomLevel, setZoomLevel] = useState(100);
  // State for canvas container size
  const [canvasSize, setCanvasSize] = useState({ width: 512, height: 512 });
  // State for tracking if templates exist
  const [templatesExist, setTemplatesExist] = useState(true);

  // State for poster text input fields
  const [posterName1, setPosterName1] = useState('');
  const [posterName2, setPosterName2] = useState('');
  const [posterName3, setPosterName3] = useState('');
  const [posterTitle, setPosterTitle] = useState('');
  const [posterSubtitle, setPosterSubtitle] = useState('');

  // Check if templates exist on the canvas
  const checkTemplatesExist = useCallback(() => {
    if (!props.canvasRef.current) return false;
    const canvas = props.canvasRef.current;
    const objects = canvas.getObjects();
    
    const hasTemplates = objects.some((obj) => {
      if (obj.type === 'textbox') {
        if (obj._templateId || props.workshopConfig.templateStrings.includes(obj.text)) {
          return true;
        }
      }
      return false;
    });
    
    return hasTemplates;
  }, [props.canvasRef]);

  // Update templatesExist state when canvas changes
  useEffect(() => {
    if (!props.canvasRef.current) return;
    
    const canvas = props.canvasRef.current;
    
    const handleCanvasChange = () => {
      // Use setTimeout to ensure state updates after canvas has fully processed the change
      setTimeout(() => {
        const hasTemplates = checkTemplatesExist();
        setTemplatesExist(hasTemplates);
      }, 50);
    };
    
    // Check on mount and when objects are added
    canvas.on('object:added', handleCanvasChange);
    canvas.on('object:removed', handleCanvasChange);
    
    // Initial check
    handleCanvasChange();
    
    return () => {
      canvas.off('object:added', handleCanvasChange);
      canvas.off('object:removed', handleCanvasChange);
    };
  }, [props.canvasRef, checkTemplatesExist]);

  // Check templates and save canvas when tab becomes active
  useEffect(() => {
    if (props.activeTab === 'poster' && props.canvasRef.current) {
      const hasTemplates = checkTemplatesExist();
      setTemplatesExist(hasTemplates);
      
      // Also check after a delay to catch templates that may be added after tab switch,
      // then persist the canvas state so it survives a refresh
      setTimeout(() => {
        const hasTemplatesDelayed = checkTemplatesExist();
        setTemplatesExist(hasTemplatesDelayed);
        syncPosterInputsToCanvas();
        if (hasLoadedFromStorage.current && props.canvasRef.current) {
          try {
            const json = props.canvasRef.current.toJSON(['_templateId']);
            localStorage.setItem("slide", JSON.stringify(json));
          } catch (e) { /* ignore */ }
        }
      }, 500);

      // Second sync for slower image loads where templates aren't ready at 500ms
      setTimeout(() => {
        syncPosterInputsToCanvas();
      }, 1000);
    }
  }, [props.activeTab, checkTemplatesExist, props.canvasRef]);

  // Check templates when readyForPoster changes (when new image is created)
  useEffect(() => {
    if (props.readyForPoster && props.canvasRef.current) {
      // Multiple checks with delays to ensure templates have been added to canvas
      const checkIntervals = [100, 300, 500, 800];
      checkIntervals.forEach(delay => {
        setTimeout(() => {
          const hasTemplates = checkTemplatesExist();
          setTemplatesExist(hasTemplates);
        }, delay);
      });
    }
  }, [props.readyForPoster, checkTemplatesExist, props.canvasRef]);

  // Periodic check while on poster tab to ensure button state is correct
  useEffect(() => {
    if (props.activeTab !== 'poster' || !props.canvasRef.current) return;
    
    const intervalId = setInterval(() => {
      const hasTemplates = checkTemplatesExist();
      setTemplatesExist(hasTemplates);
    }, 1000); // Check every second
    
    return () => clearInterval(intervalId);
  }, [props.activeTab, props.canvasRef, checkTemplatesExist]);

  const pushSlideToServer = () => {
    if (hasLoadedFromStorage.current) {
      const objects = props.canvasRef.current.getObjects();
      if (objects.length > 0) {
        let json = props.canvasRef.current.toJSON(['_templateId']);
        try {
          localStorage.setItem("slide", JSON.stringify(json));
        } catch (e) {
          // localStorage may be full; ignore quota errors
        }
      }
    }

    const data = {
      password: localStorage.getItem("frontend-password"),
      student_id: localStorage.getItem("nn-node"),
      slide: props.canvasRef.current.toDataURL({ format: "png", quality: 1 }),
      workshop_id: props.workshopId,
    };

    fetch("/submit-slide", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });

    lastPushTimeRef.current = Date.now();
  };

  const restorePosterInputsFromStorage = () => {
    try {
      const saved = localStorage.getItem("poster-text-inputs");
      if (!saved) return;
      const data = JSON.parse(saved);
      if (data.name1) setPosterName1(data.name1);
      if (data.name2) setPosterName2(data.name2);
      if (data.name3) setPosterName3(data.name3);
      if (data.title) setPosterTitle(data.title);
      if (data.subtitle) setPosterSubtitle(data.subtitle);
    } catch (e) {
      // ignore parse errors
    }
  };

  const maybeLoadFromStorage = () => {
    const savedSlide = localStorage.getItem("slide");
    if (
      hasLoadedFromStorage.current === false &&
      savedSlide &&
      props.canvasRef.current
    ) {
      const slide = JSON.parse(savedSlide);
      if (slide && slide.objects && slide.objects.length > 0) {
        props.canvasRef.current.loadFromJSON(savedSlide).then(() => {
          // Re-apply _templateId from the saved JSON since Fabric.js may
          // drop custom properties during deserialization
          const canvasObjects = props.canvasRef.current.getObjects();
          slide.objects.forEach((savedObj, index) => {
            if (savedObj._templateId && canvasObjects[index]) {
              canvasObjects[index]._templateId = savedObj._templateId;
            }
          });

          props.canvasRef.current.renderAll();
          restorePosterInputsFromStorage();
          setTimeout(() => {
            const hasTemplates = checkTemplatesExist();
            setTemplatesExist(hasTemplates);
          }, 100);
        });
      }
    }
    hasLoadedFromStorage.current = true;
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      // This code runs on the next frame after the component is rendered.
      maybeLoadFromStorage();
    });
  }, []);

  // This function checks if it can push immediately or needs to wait.
  const schedulePush = () => {
    const now = Date.now();
    const timeSinceLastPush = now - lastPushTimeRef.current;

    if (timeSinceLastPush >= PUSH_INTERVAL) {
      pushSlideToServer();
    } else if (!timerRef.current) {
      const delay = PUSH_INTERVAL - timeSinceLastPush;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        pushSlideToServer();
      }, delay);
    }
  };

  // Download canvas as PNG
  const handleDownloadPNG = () => {
    if (!props.canvasRef) return;
    const dataURL = props.canvasRef.current.toDataURL({
      format: "png",
      quality: 1,
    });
    const link = document.createElement("a");
    link.href = dataURL;
    link.download = "canvas.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download canvas as SVG
  const handleDownloadSVG = () => {
    if (!props.canvasRef.current) return;
    const svg = props.canvasRef.current.toSVG();
    const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "canvas.svg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Save state function
  const saveState = () => {
    if (!props.canvasRef.current || isRestoring.current) return;
    const json = props.canvasRef.current.toJSON(['_templateId']);
    // Strip large base64 image src from undo/redo entries to prevent memory bloat
    const lightJson = JSON.parse(JSON.stringify(json));
    lightJson.objects = lightJson.objects.map(obj => {
      if (obj.type === 'image' && obj.src && obj.src.startsWith('data:')) {
        return { ...obj, src: '' };
      }
      return obj;
    });
    if (currentStateJson.current !== null) {
      undoStack.current.push(currentStateJson.current);
    }
    currentStateJson.current = lightJson;
    redoStack.current = [];

    if (undoStack.current.length > MAX_UNDO_STEPS) {
      undoStack.current.shift();
    }

    schedulePush();
  };

  const afterRender = () => {
    if (isRestoring.current) {
      isRestoring.current = false;
    }
  };

  useEffect(() => {
    if (!canvasEl.current) return;
    // Create canvas with preserveObjectStacking so that selection doesn't automatically change z-order.
    const canvas = new Canvas(canvasEl.current, {
      preserveObjectStacking: true,
    });
    props.canvasRef.current = canvas;
    
    // Set CSS dimensions to match the calculated size while keeping pixel dimensions at 1024x1024
    canvas.setDimensions({ width: 1024, height: 1024 }, { cssOnly: true });
    canvas.setDimensions({ width: canvasSize.width, height: canvasSize.height }, { cssOnly: true });

    // Ensure canvas gets focus when clicked
    canvasEl.current.addEventListener('click', () => {
      canvasEl.current.focus();
    });

    saveState();

    const handleSelection = (e) => {
      const activeObjects = canvas.getActiveObjects();
      // When a single object is selected, update the toolbar state.
      // For multiple objects, you may choose to show mixed values.
      if (activeObjects.length === 1) {
        const selected = activeObjects[0];
        setSelectedObj(selected);
        if (selected.type === "textbox") {
          setTextProperties({
            fontSize: selected.fontSize || 20,
            fill: selected.fill || "#000000",
            fontFamily: selected.fontFamily || "Roboto",
            fontStyle: selected.fontStyle || "normal",
            fontWeight: selected.fontWeight || "normal",
            stroke: selected.stroke || "#000000",
            strokeWidth: selected.strokeWidth || 0,
            textAlign: selected.textAlign || "left",
          });
        } else {
          setShapeProperties({
            fill: selected.fill || "#ffffff",
            stroke: selected.stroke || "#000000",
            strokeWidth: selected.strokeWidth || 1,
          });
        }
      } else {
        // For multiple selection, clear the single-object state.
        setSelectedObj(null);
      }
    };

    canvas.on("selection:created", handleSelection);
    canvas.on("selection:updated", handleSelection);
    canvas.on("selection:cleared", () => setSelectedObj(null));

    canvas.on("object:modified", saveState);
    canvas.on("object:added", saveState);
    canvas.on("object:removed", saveState);
    canvas.on("after:render", afterRender);

    // Zoom and Pan functionality
    if (ENABLE_ZOOM) {
      canvas.on('mouse:wheel', function(opt) {
        // Only zoom if Shift, Ctrl, or Alt key is pressed
        if (!opt.e.shiftKey && !opt.e.ctrlKey && !opt.e.altKey) {
          return;
        }
        
        var delta = opt.e.deltaY;
        var zoom = canvas.getZoom();
        zoom *= 0.999 ** delta;
        if (zoom > 5) zoom = 5; // Prevent zoom above 500%
        if (zoom < 1) zoom = 1; // Prevent zoom below 100%
        canvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);
        opt.e.preventDefault();
        opt.e.stopPropagation();
        
        // Update zoom level state
        setZoomLevel(Math.round(zoom * 100));
        
        // Limit panning area based on zoom level
        var vpt = this.viewportTransform;
        if (zoom < 400 / 1024) {
          vpt[4] = 200 - 1024 * zoom / 2;
          vpt[5] = 200 - 1024 * zoom / 2;
        } else {
          if (vpt[4] >= 0) {
            vpt[4] = 0;
          } else if (vpt[4] < canvas.getWidth() - 1024 * zoom) {
            vpt[4] = canvas.getWidth() - 1024 * zoom;
          }
          if (vpt[5] >= 0) {
            vpt[5] = 0;
          } else if (vpt[5] < canvas.getHeight() - 1024 * zoom) {
            vpt[5] = canvas.getHeight() - 1024 * zoom;
          }
        }
      });
    }

    canvas.on('mouse:down', function(opt) {
      var evt = opt.e;
      if (evt.altKey === true || evt.button === 1) { // Alt key OR middle mouse button
        this.isDragging = true;
        this.selection = false;
        this.defaultCursor = this.defaultCursor || this.getCursor();
        this.setCursor('move');
        this.lastPosX = evt.clientX;
        this.lastPosY = evt.clientY;
      }
    });

    canvas.on('mouse:move', function(opt) {
      if (this.isDragging) {
        var e = opt.e;
        var vpt = this.viewportTransform;
        vpt[4] += e.clientX - this.lastPosX;
        vpt[5] += e.clientY - this.lastPosY;
        
        // Apply boundary constraints to panning
        var zoom = this.getZoom();
        if (zoom < 400 / 1024) {
          vpt[4] = 200 - 1024 * zoom / 2;
          vpt[5] = 200 - 1024 * zoom / 2;
        } else {
          if (vpt[4] >= 0) {
            vpt[4] = 0;
          } else if (vpt[4] < this.getWidth() - 1024 * zoom) {
            vpt[4] = this.getWidth() - 1024 * zoom;
          }
          if (vpt[5] >= 0) {
            vpt[5] = 0;
          } else if (vpt[5] < this.getHeight() - 1024 * zoom) {
            vpt[5] = this.getHeight() - 1024 * zoom;
          }
        }
        
        this.requestRenderAll();
        this.lastPosX = e.clientX;
        this.lastPosY = e.clientY;
      }
    });

    canvas.on('mouse:up', function(opt) {
      if (this.isDragging) {
        // on mouse up we want to recalculate new interaction
        // for all objects, so we call setViewportTransform
        this.setViewportTransform(this.viewportTransform);
        this.isDragging = false;
        this.selection = true;
        this.setCursor(this.defaultCursor || 'default');
      }
    });

    return () => {
      canvas.dispose();
    };
  }, []);

  // Save canvas to localStorage immediately before page unload (refresh/close)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!props.canvasRef.current || !hasLoadedFromStorage.current) return;
      try {
        const json = props.canvasRef.current.toJSON(['_templateId']);
        localStorage.setItem("slide", JSON.stringify(json));
      } catch (e) {
        // ignore quota errors
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // When changing text properties (like font, bold, italic, alignment), update all text objects in the selection
  const handleChange = (prop, value) => {
    setTextProperties((prev) => ({ ...prev, [prop]: value }));
    const canvas = props.canvasRef.current;
    const activeObjects = canvas.getActiveObjects();
    activeObjects.forEach((obj) => {
      if (obj.type === "textbox") {
        obj.set(prop, value);
      }
    });
    canvas.renderAll();
  };

  // When changing shape properties, update all non-text objects in the selection
  const handleShapeChange = (prop, value) => {
    setShapeProperties((prev) => ({ ...prev, [prop]: value }));
    const canvas = props.canvasRef.current;
    const activeObjects = canvas.getActiveObjects();
    activeObjects.forEach((obj) => {
      if (obj.type !== "textbox") {
        obj.set(prop, value);
      }
    });
    canvas.renderAll();
  };

  // Undo/Redo handlers
  const handleUndo = useCallback(() => {
    if (undoStack.current.length === 0 || isRestoring.current) return;
    const currentState = props.canvasRef.current.toJSON(['_templateId']);
    redoStack.current.push(currentState);
    const prevState = undoStack.current.pop();

    if (prevState === undefined) return;

    isRestoring.current = true;
    props.canvasRef.current.loadFromJSON(prevState).then(() => {
      props.canvasRef.current.renderAll();
      isRestoring.current = false;
    });
  }, []);

  const handleRedo = useCallback(() => {
    if (redoStack.current.length === 0) return;
    const currentState = props.canvasRef.current.toJSON(['_templateId']);
    undoStack.current.push(currentState);
    const nextState = redoStack.current.pop();
    isRestoring.current = true;
    props.canvasRef.current.loadFromJSON(nextState).then(() => {
      props.canvasRef.current.renderAll();
      isRestoring.current = false;
    });
  }, []);

  // Zoom control functions
  const handleZoomChange = (option) => {
    if (!props.canvasRef.current) return;
    const newZoom = option.value / 100;
    if (newZoom < 1 || newZoom > 5) return; // Prevent zoom below 100% or above 500%
    props.canvasRef.current.setZoom(newZoom);
    setZoomLevel(option.value);
    
    // Apply boundary constraints after zoom change
    const canvas = props.canvasRef.current;
    const vpt = canvas.viewportTransform;
    if (newZoom < 400 / 1024) {
      vpt[4] = 200 - 1024 * newZoom / 2;
      vpt[5] = 200 - 1024 * newZoom / 2;
    } else {
      if (vpt[4] >= 0) {
        vpt[4] = 0;
      } else if (vpt[4] < canvas.getWidth() - 1024 * newZoom) {
        vpt[4] = canvas.getWidth() - 1024 * newZoom;
      }
      if (vpt[5] >= 0) {
        vpt[5] = 0;
      } else if (vpt[5] < canvas.getHeight() - 1024 * newZoom) {
        vpt[5] = canvas.getHeight() - 1024 * newZoom;
      }
    }
    canvas.renderAll();
  };

  const handleResetZoom = () => {
    if (!props.canvasRef.current) return;
    props.canvasRef.current.setViewportTransform([1, 0, 0, 1, 0, 0]);
    setZoomLevel(100);
    props.canvasRef.current.renderAll();
  };

  // Function to calculate optimal canvas size based on screen size
  const calculateCanvasSize = useCallback(() => {
    const maxWidth = Math.min(window.innerWidth - 80, 1024);
    const maxHeight = Math.min(window.innerHeight - 150, 1024);
    
    const size = Math.min(maxWidth, maxHeight);
    const finalSize = Math.max(size, 300);
    
    setCanvasSize({ width: finalSize, height: finalSize });
  }, []);

  // Resize canvas when window size changes
  useEffect(() => {
    calculateCanvasSize();
    
    const handleResize = () => {
      calculateCanvasSize();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateCanvasSize]);

  // Update canvas CSS dimensions when canvas size changes
  useEffect(() => {
    if (props.canvasRef.current) {
      props.canvasRef.current.setDimensions({ width: canvasSize.width, height: canvasSize.height }, { cssOnly: true });
      props.canvasRef.current.renderAll();
    }
  }, [canvasSize]);

  // Copy handler now supports multiple selection.
  const handleCopy = () => {
    const canvas = props.canvasRef.current;
    const activeObjects = canvas.getActiveObjects();
    if (activeObjects.length === 0) return;
    if (activeObjects.length === 1) {
      activeObjects[0].clone().then((clonedObj) => {
        clipboard.current = clonedObj;
      });
    } else {
      // Create an ActiveSelection from the selected objects and clone that.
      const selection = new ActiveSelection(activeObjects, { canvas });
      selection.clone().then((clonedObj) => {
        clipboard.current = clonedObj;
      });
    }
  };

  // Paste handler remains largely the same.
  const handlePaste = () => {
    if (!clipboard.current) return;
    clipboard.current.clone().then((clonedObj) => {
      const canvas = props.canvasRef.current;
      canvas.discardActiveObject();
      clonedObj.set({
        left: clonedObj.left + 10,
        top: clonedObj.top + 10,
        evented: true,
      });
      if (clonedObj instanceof ActiveSelection) {
        clonedObj.canvas = canvas;
        clonedObj.forEachObject((obj) => {
          canvas.add(obj);
        });
        clonedObj.setCoords();
      } else {
        canvas.add(clonedObj);
      }
      // Update clipboard position for subsequent pastes.
      clipboard.current.left += 10;
      clipboard.current.top += 10;
      canvas.setActiveObject(clonedObj);
      canvas.requestRenderAll();
    });
  };

  // Element creation handlers (these create new objects so they don't need multi-selection logic)
  const handleAddText = () => {
    if (props.canvasRef.current) {
      const newTextbox = new Textbox("New Text", {
        fontSize: textProperties.fontSize,
        fill: textProperties.fill,
        fontFamily: textProperties.fontFamily,
        fontStyle: textProperties.fontStyle,
        fontWeight: textProperties.fontWeight,
        stroke: textProperties.stroke,
        strokeWidth: 0,
        textAlign: textProperties.textAlign,
        left: 50,
        top: 50,
        width: 200,
      });
      props.canvasRef.current.add(newTextbox);
      props.canvasRef.current.setActiveObject(newTextbox);
      props.canvasRef.current.renderAll();
    }
  };

  const handleAddRect = () => {
    if (props.canvasRef.current) {
      const newRect = new Rect({
        width: 100,
        height: 60,
        fill: shapeProperties.fill,
        stroke: shapeProperties.stroke,
        strokeWidth: shapeProperties.strokeWidth,
        left: 50,
        top: 50,
      });
      props.canvasRef.current.add(newRect);
      props.canvasRef.current.setActiveObject(newRect);
      props.canvasRef.current.renderAll();
    }
  };

  const handleAddCircle = () => {
    if (props.canvasRef.current) {
      const newCircle = new Circle({
        radius: 30,
        fill: shapeProperties.fill,
        stroke: shapeProperties.stroke,
        strokeWidth: shapeProperties.strokeWidth,
        left: 50,
        top: 50,
      });
      props.canvasRef.current.add(newCircle);
      props.canvasRef.current.setActiveObject(newCircle);
      props.canvasRef.current.renderAll();
    }
  };

  const handleAddTriangle = () => {
    if (props.canvasRef.current) {
      const newTriangle = new Triangle({
        width: 80,
        height: 80,
        fill: shapeProperties.fill,
        stroke: shapeProperties.stroke,
        strokeWidth: shapeProperties.strokeWidth,
        left: 50,
        top: 50,
      });
      props.canvasRef.current.add(newTriangle);
      props.canvasRef.current.setActiveObject(newTriangle);
      props.canvasRef.current.renderAll();
    }
  };

  const handleAddLine = () => {
    if (props.canvasRef.current) {
      const newLine = new Line([50, 50, 150, 150], {
        stroke: shapeProperties.stroke,
        strokeWidth: shapeProperties.strokeWidth,
        x1: 50,
        y1: 50,
        x2: 150,
        y2: 150,
      });
      props.canvasRef.current.add(newLine);
      props.canvasRef.current.setActiveObject(newLine);
      props.canvasRef.current.renderAll();
    }
  };

  // Layer control handlers now update every active object.
  const handleLayerUp = () => {
    const canvas = props.canvasRef.current;

    const activeObjects = canvas.getActiveObjects();

    // Need to sort objects by their current stacking order first.
    const sortedActiveObjects = activeObjects.sort((a, b) => {
      return canvas.getObjects().indexOf(b) - canvas.getObjects().indexOf(a);
    });

    sortedActiveObjects.forEach((obj) => {
      canvas.bringObjectForward(obj);
    });
    canvas.renderAll();
  };

  const handleLayerDown = () => {
    const canvas = props.canvasRef.current;
    const activeObjects = canvas.getActiveObjects();

    // Need to sort objects by their current stacking order first.
    const sortedActiveObjects = activeObjects.sort((a, b) => {
      return canvas.getObjects().indexOf(a) - canvas.getObjects().indexOf(b);
    });

    sortedActiveObjects.forEach((obj) => {
      canvas.sendObjectBackwards(obj);
    });
    canvas.renderAll();
  };

  // Delete handler now removes all active objects.
  const handleDelete = () => {
    if (!props.canvasRef.current) return;
    const canvas = props.canvasRef.current;
    const activeObjects = canvas.getActiveObjects();
    activeObjects.forEach((obj) => {
      canvas.remove(obj);
    });
    canvas.discardActiveObject();
    canvas.renderAll();
    setSelectedObj(null);
  };

  // Delete all template elements (white rectangles and template text boxes)
  const handleDeleteTemplates = () => {
    if (!props.canvasRef.current) return;
    const canvas = props.canvasRef.current;
    const objects = canvas.getObjects();
    
    const templatesToRemove = objects.filter((obj) => {
      if (obj.type === 'textbox') {
        if (obj._templateId || props.workshopConfig.templateStrings.includes(obj.text)) {
          return true;
        }
      }
      return false;
    });
    
    templatesToRemove.forEach((obj) => {
      canvas.remove(obj);
    });
    
    canvas.discardActiveObject();
    canvas.renderAll();
    
    setPosterName1('');
    setPosterName2('');
    setPosterName3('');
    setPosterTitle('');
    setPosterSubtitle('');
    try { localStorage.removeItem("poster-text-inputs"); } catch (e) { /* ignore */ }

    setTimeout(() => {
      setTemplatesExist(false);
    }, 50);
  };

  // Add template elements back to canvas
  const handleAddTemplates = () => {
    if (!props.canvasRef.current) return;
    const canvas = props.canvasRef.current;
    
    const objects = canvas.getObjects();
    const img = objects.find(obj => obj.type === 'image');
    
    if (!img) return;
    
    const imgScaledWidth = img.width * img.scaleX;
    const imgScaledHeight = img.height * img.scaleY;

    let imgLeftCorner = img.left;
    let imgTopCorner = img.top;
    if (img.originX === 'center') {
      imgLeftCorner -= imgScaledWidth / 2;
    } else if (img.originX === 'right') {
      imgLeftCorner -= imgScaledWidth;
    }
    if (img.originY === 'center') {
      imgTopCorner -= imgScaledHeight / 2;
    } else if (img.originY === 'bottom') {
      imgTopCorner -= imgScaledHeight;
    }

    props.workshopConfig.templateElements.forEach((elem) => {
      const pos = resolvePosition(elem.position, imgLeftCorner, imgTopCorner, imgScaledWidth, imgScaledHeight);
      const textbox = new Textbox(elem.text, {
        left: pos.left,
        top: pos.top,
        width: pos.width,
        originX: 'left',
        originY: 'top',
        fontSize: elem.fontSize,
        fill: elem.fill,
        fontFamily: elem.fontFamily,
        fontWeight: elem.fontWeight || 'normal',
        textAlign: elem.textAlign || 'center',
        stroke: elem.stroke,
        strokeWidth: elem.strokeWidth,
        ...(elem.id ? { _templateId: elem.id } : {}),
      });
      canvas.add(textbox);
    });

    canvas.renderAll();
    
    setPosterName1('');
    setPosterName2('');
    setPosterName3('');
    setPosterTitle('');
    setPosterSubtitle('');
    try { localStorage.removeItem("poster-text-inputs"); } catch (e) { /* ignore */ }

    setTimeout(() => {
      setTemplatesExist(true);
    }, 50);
  };

  const tagAllTemplateObjects = () => {
    if (!props.canvasRef.current) return;
    const objects = props.canvasRef.current.getObjects();
    const templateElements = props.workshopConfig.templateElements;

    const byText = {};
    templateElements.forEach((elem) => {
      if (!elem.id) return;
      if (!byText[elem.text]) byText[elem.text] = [];
      byText[elem.text].push(elem.id);
    });

    Object.entries(byText).forEach(([text, ids]) => {
      const untagged = objects.filter(obj => obj.type === 'textbox' && obj.text === text && !obj._templateId);
      if (untagged.length > 0) {
        untagged.sort((a, b) => a.left - b.left);
        untagged.forEach((obj, i) => {
          if (i < ids.length && !objects.some(o => o._templateId === ids[i])) {
            obj._templateId = ids[i];
          }
        });
      }
    });
  };

  // Find a template textbox on the canvas by its _templateId
  const findTemplateObject = (templateId) => {
    if (!props.canvasRef.current) return null;
    tagAllTemplateObjects();
    const objects = props.canvasRef.current.getObjects();
    return objects.find(obj => obj.type === 'textbox' && obj._templateId === templateId) || null;
  };

  const syncPosterInputsToCanvas = () => {
    if (!props.canvasRef.current) return;
    let data;
    try {
      const saved = localStorage.getItem("poster-text-inputs");
      if (!saved) return;
      data = JSON.parse(saved);
    } catch (e) { return; }

    let changed = false;
    const entries = [
      { id: 'name1', value: data.name1 },
      { id: 'name2', value: data.name2 },
      { id: 'name3', value: data.name3 },
      { id: 'title', value: data.title },
      { id: 'subtitle', value: data.subtitle },
    ];
    entries.forEach(({ id, value }) => {
      if (!value) return;
      const obj = findTemplateObject(id);
      if (obj && obj.text !== value) {
        obj.set('text', value);
        changed = true;
      }
    });
    if (changed) {
      props.canvasRef.current.renderAll();
    }
  };

  const savePosterInputsToStorage = (overrides = {}) => {
    const data = {
      name1: posterName1,
      name2: posterName2,
      name3: posterName3,
      title: posterTitle,
      subtitle: posterSubtitle,
      ...overrides,
    };
    try {
      localStorage.setItem("poster-text-inputs", JSON.stringify(data));
    } catch (e) {
      // ignore quota errors
    }
  };

  const handlePosterInputChange = (templateId, placeholderText, value, setter) => {
    setter(value);
    savePosterInputsToStorage({ [templateId]: value });
    const obj = findTemplateObject(templateId);
    if (obj) {
      obj.set('text', value || placeholderText);
      props.canvasRef.current.renderAll();
    }
  };

  // Fixed image upload handler using promise-based FabricImage.fromURL
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (f) => {
      const data = f.target.result;
      FabricImage.fromURL(data)
        .then((img) => {
          img.set({ left: 100, top: 100 });
          props.canvasRef.current.add(img);
          props.canvasRef.current.setActiveObject(img);
          props.canvasRef.current.renderAll();
          fileInputRef.current.value = "";
        })
        .catch((error) => {
          console.error("Error loading uploaded image:", error);
        });
    };
    reader.readAsDataURL(file);
  };

  const handleUploadImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const isText = selectedObj && selectedObj.type === "textbox";
  const currentFillColor = isText ? textProperties.fill : shapeProperties.fill;
  const currentStrokeColor = isText
    ? textProperties.stroke
    : shapeProperties.stroke;

  // Global keyboard shortcuts including arrow key movement.
  useEffect(() => {
    
    const handleKeyDown = (e) => {
      if (props.activeTab != "poster") {
        return;
      }
      
      const activeTag = document.activeElement.tagName;
      
      if (activeTag === "INPUT" || activeTag === "TEXTAREA") {
        return;
      }

      if (e.ctrlKey && !e.altKey) {
        const key = e.key.toLowerCase();
        if (key === "z") {
          e.shiftKey ? handleRedo() : handleUndo();
          e.preventDefault();
        } else if (key === "y") {
          handleRedo();
          e.preventDefault();
        } else if (key === "b") {
          if (isText) {
            handleChange(
              "fontWeight",
              textProperties.fontWeight === "bold" ? "normal" : "bold"
            );
          }
          e.preventDefault();
        } else if (key === "i") {
          if (isText) {
            handleChange(
              "fontStyle",
              textProperties.fontStyle === "italic" ? "normal" : "italic"
            );
          }
          e.preventDefault();
        } else if (key === "c") {
          handleCopy();
          e.preventDefault();
        } else if (key === "v") {
          handlePaste();
          e.preventDefault();
        }
      }
      if (e.ctrlKey && e.altKey) {
        if (e.key === "ArrowUp") {
          handleLayerUp();
          e.preventDefault();
        } else if (e.key === "ArrowDown") {
          handleLayerDown();
          e.preventDefault();
        }
      }
      if (!e.ctrlKey && !e.altKey && (e.key === "Delete" || e.keyCode === 46 || e.key === "Backspace" || e.keyCode === 8)) {
        handleDelete();
        e.preventDefault();
      }

      const obj = props.canvasRef.current.getActiveObject();
      if (
        !e.ctrlKey &&
        !e.altKey &&
        ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)
      ) {
        if (obj) {
          const step = e.shiftKey ? 20 : 5;
          let { left, top } = obj;
          if (e.key === "ArrowUp") top -= step;
          else if (e.key === "ArrowDown") top += step;
          else if (e.key === "ArrowLeft") left -= step;
          else if (e.key === "ArrowRight") left += step;
          obj.set({ left, top });
          obj.setCoords();
          props.canvasRef.current.renderAll();
          e.preventDefault();
        }
      }
      if (
        !e.ctrlKey &&
        e.altKey &&
        ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)
      ) {
        if (obj) {
          const fineStep = 1;
          let { left, top } = obj;
          if (e.key === "ArrowUp") top -= fineStep;
          else if (e.key === "ArrowDown") top += fineStep;
          else if (e.key === "ArrowLeft") left -= fineStep;
          else if (e.key === "ArrowRight") left += fineStep;
          obj.set({ left, top });
          obj.setCoords();
          props.canvasRef.current.renderAll();
          e.preventDefault();
        }
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [props.activeTab, isText, textProperties, handleRedo, handleUndo]);

  // onKeyDown for the font select (arrow keys to change font immediately)
  const handleFontKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      let currentIndex = fontOptions.findIndex(
        (option) => option.value === textProperties.fontFamily
      );
      if (currentIndex === -1) currentIndex = 0;
      currentIndex =
        e.key === "ArrowDown"
          ? (currentIndex + 1) % fontOptions.length
          : (currentIndex - 1 + fontOptions.length) % fontOptions.length;
      const newFont = fontOptions[currentIndex].value;
      handleChange("fontFamily", newFont);
      e.preventDefault();
    }
  };

  let everythingVis = "block";
  let goToTab = <></>;

  if (!props.readyForPoster) {
    everythingVis = "none";
    goToTab = (
      <h4 className="text-center mt-5" style={{ marginBottom: "24rem" }}>
        Go to the{" "}
        <Button
          className="btn-nav-link"
          onClick={() => props.switchTab('dalle')}
          style={{
            margin: "0 5px",
            padding: "4px 12px",
            fontSize: "14px",
            display: "inline-block",
            whiteSpace: "nowrap",
            backgroundColor: "white",
            borderColor: "#f05f40",
            color: "#f05f40",
          }}
        >
          Image Generation
        </Button>{" "}
        tab to create your film poster
      </h4>
    );
  }

  return (
    <>
      {goToTab}
      <div style={{ display: everythingVis }}>
        {/* Main container with light gray border */}
        <div style={{ 
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '20px',
          backgroundColor: '#fff'
        }}>
          {/* Instruction Box */}
          <div style={{
            border: '3px solid #f05f40',
            borderRadius: '8px',
            padding: '12px 20px',
            marginBottom: '15px',
            backgroundColor: '#fff',
            fontFamily: 'Roboto, sans-serif'
          }}>
          <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>
            Instructions:
          </h3>
          <h4 style={{ color: "#f05f40ff", fontWeight: "bold", margin: "0 0 5px 0" }}>
            {props.workshopConfig.instructions}
          </h4>
          <p style={{ color: "#f05f40ff", fontWeight: "bold", margin: "0" }}>
            {props.workshopConfig.instructionsSubtitle}
          </p>
        </div>
        
        {/* Top Toolbar */}
        <Container fluid className="p-1 bg-light">
          {/* Poster Text Input Fields + Delete/Add Templates Button */}
          {templatesExist ? (
            <div style={{
              backgroundColor: '#d6d6d6',
              borderRadius: '4px',
              padding: '0.25rem 0.75rem',
              marginBottom: '0.4rem',
            }}>
              <Row className="align-items-center" style={{ marginBottom: '2px' }}>
                <Col xs="auto" style={{ fontSize: '0.7rem', fontWeight: 'bold', width: '55px', minWidth: '55px', maxWidth: '55px', paddingRight: 0 }}>{props.workshopConfig.formLabels.names}</Col>
                <Col>
                  <div style={{ display: 'flex', gap: '0.35rem' }}>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder={props.workshopConfig.formPlaceholders.name}
                      value={posterName1}
                      onChange={(e) => handlePosterInputChange('name1', props.workshopConfig.formPlaceholders.name, e.target.value, setPosterName1)}
                      style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem', height: '1.5rem' }}
                    />
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder={props.workshopConfig.formPlaceholders.name}
                      value={posterName2}
                      onChange={(e) => handlePosterInputChange('name2', props.workshopConfig.formPlaceholders.name, e.target.value, setPosterName2)}
                      style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem', height: '1.5rem' }}
                    />
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder={props.workshopConfig.formPlaceholders.name}
                      value={posterName3}
                      onChange={(e) => handlePosterInputChange('name3', props.workshopConfig.formPlaceholders.name, e.target.value, setPosterName3)}
                      style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem', height: '1.5rem' }}
                    />
                  </div>
                </Col>
              </Row>
              <Row className="align-items-center" style={{ marginBottom: '2px' }}>
                <Col xs="auto" style={{ fontSize: '0.7rem', fontWeight: 'bold', width: '55px', minWidth: '55px', maxWidth: '55px', paddingRight: 0 }}>{props.workshopConfig.formLabels.title}</Col>
                <Col>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder={props.workshopConfig.formPlaceholders.title}
                    value={posterTitle}
                    onChange={(e) => handlePosterInputChange('title', props.workshopConfig.formPlaceholders.title, e.target.value, setPosterTitle)}
                    style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem', height: '1.5rem' }}
                  />
                </Col>
              </Row>
              <Row className="align-items-center" style={{ marginBottom: '2px' }}>
                <Col xs="auto" style={{ fontSize: '0.7rem', fontWeight: 'bold', width: '55px', minWidth: '55px', maxWidth: '55px', paddingRight: 0 }}>{props.workshopConfig.formLabels.subtitle}</Col>
                <Col>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder={props.workshopConfig.formPlaceholders.subtitle}
                    value={posterSubtitle}
                    onChange={(e) => handlePosterInputChange('subtitle', props.workshopConfig.formPlaceholders.subtitle, e.target.value, setPosterSubtitle)}
                    style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem', height: '1.5rem' }}
                  />
                </Col>
              </Row>
              <Row className="align-items-center" style={{ marginTop: '2px', marginBottom: '2px' }}>
                <Col xs="auto">
                  <Button
                    variant="outline-secondary"
                    onClick={handleDeleteTemplates}
                    size="sm"
                    style={{ fontSize: '0.6rem', padding: '0.1rem 0.35rem', lineHeight: 1.2 }}
                  >
                    Delete Template Elements
                  </Button>
                </Col>
              </Row>
            </div>
          ) : (
            <Row className="align-items-center mb-2">
              <Col xs="auto">
                <Button
                  variant="outline-secondary"
                  onClick={handleAddTemplates}
                  size="sm"
                >
                  Add Template Elements
                </Button>
              </Col>
            </Row>
          )}
          <Row className="align-items-center">
            <Col xs="auto" className="d-flex align-items-center flex-wrap">
              <ButtonGroup className="me-2">
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Undo (Ctrl+Z)</Tooltip>}
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleUndo}
                  >
                    <FaUndo size={20} />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Redo (Ctrl+Y or Ctrl+Shift+Z)</Tooltip>}
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleRedo}
                  >
                    <FaRedo size={20} />
                  </Button>
                </OverlayTrigger>
              </ButtonGroup>
              <ButtonGroup className="me-2">
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Add Text</Tooltip>}
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleAddText}
                  >
                    <FaFont size={20} />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Add Rectangle</Tooltip>}
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleAddRect}
                  >
                    <FaSquare size={20} />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Add Circle</Tooltip>}
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleAddCircle}
                  >
                    <FaCircle size={20} />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Add Triangle</Tooltip>}
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleAddTriangle}
                  >
                    <GiTriangleTarget size={20} />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Add Line</Tooltip>}
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleAddLine}
                  >
                    <FaMinus size={20} />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Upload Image</Tooltip>}
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleUploadImageClick}
                  >
                    <FaUpload size={20} />
                  </Button>
                </OverlayTrigger>
              </ButtonGroup>
              <ButtonGroup className="me-2">
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Layer Up (Ctrl+Alt+↑)</Tooltip>}
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleLayerUp}
                  >
                    <FaArrowUp size={20} />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Layer Down (Ctrl+Alt+↓)</Tooltip>}
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleLayerDown}
                  >
                    <FaArrowDown size={20} />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Delete object (Backspace)</Tooltip>}
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleDelete}
                  >
                    <FaTrash size={20} />
                  </Button>
                </OverlayTrigger>
              </ButtonGroup>
              <ButtonGroup className="me-2">
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Download PNG</Tooltip>}
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleDownloadPNG}
                  >
                    <FaFileImage size={20} />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Download SVG</Tooltip>}
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleDownloadSVG}
                  >
                    <FaFileCode size={20} />
                  </Button>
                </OverlayTrigger>
              </ButtonGroup>
              {/* Zoom Controls */}
              {ENABLE_ZOOM && (
                <ButtonGroup className="me-2">
                  <div style={{ width: "100px" }} className="me-2">
                    <Select
                      value={zoomOptions.find(option => option.value === zoomLevel) || { value: zoomLevel, label: `${zoomLevel}%` }}
                      onChange={handleZoomChange}
                      options={zoomOptions}
                      isSearchable={false}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: "30px",
                          fontSize: "0.8rem",
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          padding: "0 6px",
                        }),
                        indicatorsContainer: (provided) => ({
                          ...provided,
                          height: "30px",
                        }),
                      }}
                    />
                  </div>
                  <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip>Fit to Screen</Tooltip>}
                  >
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleResetZoom}
                    >
                      <FaExpand size={16} />
                    </Button>
                  </OverlayTrigger>
                </ButtonGroup>
              )}
            </Col>
          </Row>
          <Row>
            <Col xs="auto" className="d-flex align-items-center flex-wrap">
            Fill: 
              <Dropdown style={{ margin: 0, padding: 0 }}>
                <Dropdown.Toggle variant="secondary-outline">
                  <span
                    style={{
                      display: "inline-block",
                      width: 32,
                      height: 32,
                      padding: 0,
                      margin: 0,
                      backgroundColor: currentFillColor,
                      border: "1px solid #ccc",
                      borderRadius: 4,
                      cursor: "pointer",
                      alignItems: "center",
                    }}
                  > 
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu
                  renderOnMount={true}
                  style={{ textAlign: "center" }}
                >
                  <Dropdown.ItemText>
                    <Sketch
                      color={currentFillColor}
                      disableAlpha={true}
                      onChange={(color) => {
                        if (isText) handleChange("fill", color.hex);
                        else handleShapeChange("fill", color.hex);
                      }}
                    />
                  </Dropdown.ItemText>
                </Dropdown.Menu>
              </Dropdown>

              Outline:
              <Dropdown style={{ margin: 0, padding: 0 }}>
                <Dropdown.Toggle variant="secondary-outline" >
                  <span
                    style={{
                      display: "inline-block",
                      width: 32,
                      height: 32,
                      padding: 0,
                      margin: 0,
                      backgroundColor: currentStrokeColor,
                      border: "1px solid #ccc",
                      borderRadius: 4,
                      cursor: "pointer",
                      alignItems: "center",
                    }}
                  >
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu
                  renderOnMount={true}
                  style={{ textAlign: "center" }}
                >
                  <Dropdown.ItemText>
                    <Sketch
                      color={currentStrokeColor}
                      disableAlpha={true}
                      onChange={(color) => {
                        if (isText) handleChange("stroke", color.hex);
                        else handleShapeChange("stroke", color.hex);
                      }}
                    />
                  </Dropdown.ItemText>
                </Dropdown.Menu>
              </Dropdown>

              {/* Stroke width input */}
              Outline width:
              <Form.Control
                type="number"
                size="sm"
                style={{ width: "50px", marginLeft: "5px" }}
                disabled={
                  !props.canvasRef.current ||
                  !props.canvasRef.current.getActiveObjects().length
                }
                value={
                  isText
                    ? textProperties.strokeWidth
                    : shapeProperties.strokeWidth
                }
                onChange={(e) => {
                  const newVal = parseInt(e.target.value, 10);
                  if (isText) handleChange("strokeWidth", newVal);
                  else handleShapeChange("strokeWidth", newVal);
                }}
                className="me-1"
              />

              {isText && (
                <>
                  <div style={{ width: "150px" }} className="me-1">
                    <Select
                      value={fontOptions.find(
                        (option) => option.value === textProperties.fontFamily
                      )}
                      onChange={(option) =>
                        handleChange("fontFamily", option.value)
                      }
                      options={fontOptions}
                      styles={customSelectStyles}
                      isSearchable={false}
                      onKeyDown={handleFontKeyDown}
                    />
                  </div>
                  <div style={{ width: "100px" }} className="me-1">
                    <CreatableSelect
                      value={{
                        value: textProperties.fontSize,
                        label: textProperties.fontSize.toString(),
                      }}
                      options={fontSizeOptions}
                      onChange={(option) => {
                        if (!option) return;
                        const newFontSize = parseInt(option.value, 10);
                        if (!isNaN(newFontSize) && newFontSize > 0) {
                          handleChange("fontSize", newFontSize);
                        }
                      }}
                      onCreateOption={(inputValue) => {
                        const newFontSize = parseInt(inputValue, 10);
                        if (!isNaN(newFontSize) && newFontSize > 0) {
                          handleChange("fontSize", newFontSize);
                        }
                      }}
                      isValidNewOption={(inputValue) => {
                        const num = parseInt(inputValue, 10);
                        return !isNaN(num) && num > 0 && num <= 1000;
                      }}
                      isClearable={false}
                      isSearchable={true}
                      formatCreateLabel={(inputValue) => `${inputValue}`}
                      placeholder=""
                      styles={{
                        container: (provided) => ({
                          ...provided,
                          width: "100px",
                        }),
                        control: (provided) => ({
                          ...provided,
                          minHeight: "30px",
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          padding: "0 6px",
                        }),
                        indicatorsContainer: (provided) => ({
                          ...provided,
                          height: "30px",
                        }),
                      }}
                    />
                  </div>
                  <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip>Bold (Ctrl+B)</Tooltip>}
                  >
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-1"
                      onClick={() =>
                        handleChange(
                          "fontWeight",
                          textProperties.fontWeight === "bold"
                            ? "normal"
                            : "bold"
                        )
                      }
                    >
                      <FaBold
                        size={16}
                        color={
                          textProperties.fontWeight === "bold"
                            ? "black"
                            : "gray"
                        }
                      />
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip>Italic (Ctrl+I)</Tooltip>}
                  >
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-1"
                      onClick={() =>
                        handleChange(
                          "fontStyle",
                          textProperties.fontStyle === "italic"
                            ? "normal"
                            : "italic"
                        )
                      }
                    >
                      <FaItalic
                        size={16}
                        color={
                          textProperties.fontStyle === "italic"
                            ? "black"
                            : "gray"
                        }
                      />
                    </Button>
                  </OverlayTrigger>
                  {/* Alignment Buttons */}
                  <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip>Align Left</Tooltip>}
                  >
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-1"
                      onClick={() => handleChange("textAlign", "left")}
                    >
                      <FaAlignLeft size={16} />
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip>Align Center</Tooltip>}
                  >
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-1"
                      onClick={() => handleChange("textAlign", "center")}
                    >
                      <FaAlignCenter size={16} />
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip>Align Right</Tooltip>}
                  >
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-1"
                      onClick={() => handleChange("textAlign", "right")}
                    >
                      <FaAlignRight size={16} />
                    </Button>
                  </OverlayTrigger>
                </>
              )}
            </Col>
          </Row>
        </Container>

        <div className="p-2" style={{ marginBottom: "20px", maxWidth: "100%", overflow: "hidden", display: "flex", flexDirection: "column", alignItems: window.innerWidth >= 992 ? "flex-start" : "center" }}>
          <canvas
            ref={canvasEl}
            width="1024"
            height="1024"
            tabIndex="0"
            style={{ 
              border: "1px solid black", 
              marginTop: "10px",
              width: `${canvasSize.width}px`,
              height: `${canvasSize.height}px`,
              maxWidth: "100%",
              outline: "none",
            }}
          />

          {/* Warning Box */}
          <div style={{
            padding: '6px 0',
            marginTop: '15px',
            backgroundColor: '#fff',
            fontFamily: 'Roboto, sans-serif',
            textAlign: 'center',
            width: `${canvasSize.width}px`,
            maxWidth: "100%",
            boxSizing: "border-box"
          }}>
            <p style={{ color: "#f05f40ff", fontWeight: "bold", margin: "0 auto", fontSize: `${Math.max(11, Math.min(16, canvasSize.width * 0.04))}px`, backgroundColor: "#e8e8e8", padding: "4px 14px", borderRadius: "999px", display: "inline-block", whiteSpace: "nowrap" }}>
              {props.workshopConfig.linkedText}
            </p>
          </div>
          <hr style={{ marginTop: "50px", width: "100%", alignSelf: "stretch" }} />
          <div style={{
            backgroundColor: "white",
            fontSize: "large",
            border: "2px solid #f05f40",
            borderRadius: "15px",
            padding: "10px 15px 15px 15px",
            margin: "15px 0 10px 0",
            boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)",
            width: window.innerWidth < 992 ? "100%" : "50%"
          }}>
            <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>
              Instructions:
            </h3>
            <p style={{ margin: "0" }}>
              {props.workshopConfig.nextStepTitle}
              <br />
              <br />
              Select{" "}
              <Button
                className="btn-nav-link"
                onClick={() => props.switchTab('voice-cloning')}
                style={{
                  margin: "5px 5px",
                  padding: "8px 30px",
                  fontSize: "14px",
                  display: "inline-block",
                  whiteSpace: "nowrap",
                  backgroundColor: "white",
                  borderColor: "#f05f40",
                  color: "#f05f40",
                  minWidth: "180px",
                }}
              >
                {props.workshopConfig.nextStepButtonText}
              </Button>{" "}
              to get started.
            </p>
          </div>
          <hr style={{ marginTop: "15px", width: "100%", alignSelf: "stretch" }} />
        </div>
        {/* End of main container with light gray border */}
        </div>

        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept="image/*"
          onChange={handleImageUpload}
        />
      </div>
    </>
  );
};

export { MoviePosterEditor };
